import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { createOffer, createApplication, uploadDocument } from "@/api/simpego";
import { useErrorDialog } from "@/context/ErrorDialogContext";
import { useAgentId } from "@/context/AgentIdContext";
import productData from "@/data/productData.json";
import { ProductDataSource } from "@/types/product";
import { Customer, ContactDetails, Address, OfferRequest, ApplicationRequest } from "@/types/simpego-api";
import { ProductVariant } from "@/hooks/useFunnelState";
import { format } from "date-fns";

const typedProductData: ProductDataSource = productData as ProductDataSource;
const DEFAULT_AGENT_ID = typeof window !== 'undefined' && window.__env__?.defaultAgentId ? window.__env__.defaultAgentId : "VA0000000003";

interface UseFunnelActionsProps {
  petType: "cat" | "dog" | null;
  petName: string | null;
  breed: string | null;
  birthDate: Date | undefined;
  selectedBreedType: "purebred" | "crossbreed" | "mixed" | null;
  gender: "male" | "female" | "unknown" | null;
  isCastrated: boolean;
  isHouseCat: boolean;
  selectedDogSize: "small" | "large" | null;
  chipNumber: string | null;
  selectedProduct: ProductVariant | null;
  preexistingCoverage: boolean;
  worldwideCoverage: boolean;
  yearlyDeductible: number;
  coPayCoveragePercentage: number;
  paymentInterval: 1 | 2 | 4 | 12;
  paymentMethod: "credit_card" | "ESR";
  startOfInsurance: Date;
  firstDue: Date | null;
  contractId: string | null;
  contractToken: string | null;
  agentId: string | null;
  whitelabelPartner: string | null;
  dispatchContract: "customer" | "customer_and_agent" | "agent";
  dispatchInvoice: "customer" | "customer_and_agent" | "agent";
  dispatchReminder: "customer" | "customer_and_agent" | "agent";
  dispatchEmail: "customer" | "customer_and_agent" | "agent";
  agentDispatchEmail: string | null;
  contactBirthdate: Date | undefined;
  customerGender: "male" | "female" | null;
  contactFirstName: string | null; // New prop
  contactLastName: string | null;  // New prop
  offerLoadedFromUrl: boolean;
  hasLeftConfirmationAfterOfferLoad: boolean;
  consultationProtocolPendingFile: string | null;
  consultationProtocolPendingFilename: string | null;
  setConsultationProtocolDocument: (documentId: number, filename: string) => void;
  clearConsultationProtocolPending: () => void;
  handleResetFunnel: () => void;
  handlePetDetailsSubmitState: (
    submittedPetName: string,
    selectedBreed: string,
    submittedBreedLabel: string | null,
    submittedBirthDate: Date | undefined,
    submittedSelectedBreedType: "purebred" | "crossbreed" | "mixed",
    submittedGender: "male" | "female" | "unknown",
    submittedIsCastrated: boolean,
    submittedIsHouseCat: boolean,
    submittedSelectedDogSize: "small" | "large" | null,
    submittedPreexistingCoverage: boolean,
    submittedChipNumber: string | null
  ) => void;
  setBundlePrices: (prices: { S: number; M: number; L: number } | null) => void;
  setSelectedProduct: (product: ProductVariant | null) => void;
  setFinalPrice: (price: number | null) => void;
  handleContactDetailsSubmitState: (
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    submittedStreet: string,
    submittedHousenumber: string,
    city: string,
    zip: string,
    country: string,
    birthdate: Date | undefined,
    gender: "male" | "female" | ""
  ) => void;
  fetchInitialBundlePrices: (
    petTypeArg: "cat" | "dog",
    petNameArg: string,
    breedArg: string | null,
    birthDateArg: Date | undefined,
    selectedBreedTypeArg: "purebred" | "crossbreed" | "mixed",
    genderArg: "male" | "female" | "unknown",
    isCastratedArg: boolean,
    isHouseCatArg: boolean,
    selectedDogSizeArg: "small" | "large" | null,
    preexistingCoverageArg: boolean,
    customerNameArg: string,
    customerEmailArg: string,
    locale: string,
    agentIdArg: string | null
  ) => Promise<{ S: number; M: number; L: number } | null>;
  underwritingFailed: boolean;
  underwritingReason: string | null;
  currentLocale: string;
}

export const useFunnelActions = ({
  petType,
  petName,
  breed,
  birthDate,
  selectedBreedType,
  gender,
  isCastrated,
  isHouseCat,
  selectedDogSize,
  chipNumber,
  selectedProduct,
  preexistingCoverage,
  worldwideCoverage,
  yearlyDeductible,
  coPayCoveragePercentage,
  paymentInterval,
  paymentMethod,
  startOfInsurance,
  firstDue,
  contractId,
  contractToken,
  agentId,
  whitelabelPartner,
  dispatchContract,
  dispatchInvoice,
  dispatchReminder,
  dispatchEmail,
  agentDispatchEmail,
  contactBirthdate,
  customerGender,
  contactFirstName, // Destructure new prop
  contactLastName,  // Destructure new prop
  offerLoadedFromUrl,
  hasLeftConfirmationAfterOfferLoad,
  consultationProtocolPendingFile,
  consultationProtocolPendingFilename,
  setConsultationProtocolDocument,
  clearConsultationProtocolPending,
  handleResetFunnel,
  handlePetDetailsSubmitState,
  setBundlePrices,
  setSelectedProduct,
  setFinalPrice,
  handleContactDetailsSubmitState,
  fetchInitialBundlePrices,
  underwritingFailed,
  underwritingReason,
  currentLocale,
}: UseFunnelActionsProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { showErrorDialog } = useErrorDialog();
  const { agentToken } = useAgentId();
  const [isSubmittingOffer, setIsSubmittingOffer] = React.useState<boolean>(false);

  const getLocalizedPath = React.useCallback((pathKey: string) => {
    const baseRoute = i18n.getResource(currentLocale, 'translation', pathKey) as string;
    return `/${currentLocale}${baseRoute.startsWith('/') ? baseRoute : `/${baseRoute}`}`;
  }, [currentLocale, i18n]);

  const buildOfferPayload = (
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    submittedStreet: string,
    submittedHousenumber: string,
    submittedCity: string,
    submittedZip: string,
    submittedCountry: string
  ): OfferRequest | null => {
    if (!petType || !petName || !birthDate || !selectedBreedType || !gender || !selectedProduct) {
      showErrorDialog(t("common.errorCalculatingPrice"));
      return null;
    }
    if ((selectedBreedType === "purebred" || selectedBreedType === "crossbreed") && !breed) {
      showErrorDialog(t("petDetails.validation.selectBreed"));
      return null;
    }
    if (selectedBreedType === "mixed" && petType === "dog" && selectedDogSize === null) {
      showErrorDialog(t("petDetails.validation.selectDogSize"));
      return null;
    }

    const formattedStartOfInsurance = format(startOfInsurance, "yyyy-MM-dd");
    const formattedPetBirthdate = format(birthDate, "yyyy-MM-dd");
    const formattedCustomerBirthdate = contactBirthdate ? format(contactBirthdate, "yyyy-MM-dd") : "1980-01-01";
    const formattedFirstDue = firstDue ? format(firstDue, "yyyy-MM-dd") : null;
    const normalizedChipNumber = (chipNumber || "").replace(/\D/g, "").slice(0, 15);

    const customerAddress: Address = {
      street: submittedStreet,
      housenumber: submittedHousenumber || undefined,
      zip: submittedZip,
      city: submittedCity,
      countryCode: submittedCountry.toUpperCase().substring(0, 2),
    };

    const customerContactDetails: ContactDetails[] = [];
    if (email) customerContactDetails.push({ value: email, category: "email" });
    if (phone) customerContactDetails.push({ value: phone, category: "cellphone" });

    const customer: Customer = {
      objectType: "NaturalPerson",
      firstname: firstName,
      name: lastName,
      birthdate: formattedCustomerBirthdate,
      email: email,
      gender: customerGender === "male" ? "male" : "female",
      language: currentLocale.toUpperCase() as "DE" | "FR" | "IT" | "EN",
      address: customerAddress,
      contactDetails: customerContactDetails.length > 0 ? customerContactDetails : undefined,
    };

    const productDetails = typedProductData.v1[selectedProduct.id.toLowerCase() as "s" | "m" | "l"];
    const claimDeductiblePercentageUpTo9Yrs = 100 - coPayCoveragePercentage;
    const claimDeductiblePercentageOver9Yrs = productDetails.disease.claim_deductible_percentage_over_9_yrs;

    let finalBreed: string | null = null;
    let isCrossbreedFlag = false;

    if (selectedBreedType === "purebred" || selectedBreedType === "crossbreed") {
      finalBreed = breed;
      isCrossbreedFlag = selectedBreedType === "crossbreed";
    } else if (selectedBreedType === "mixed") {
      isCrossbreedFlag = false;
      if (petType === "cat") finalBreed = "mischlingskatze";
      else if (petType === "dog") finalBreed = selectedDogSize === "small" ? "mischling_klein" : "mischling_gross";
    }

    return {
      contractID: contractId || undefined,
      contractToken: contractToken || undefined,
      startOfInsurance: formattedStartOfInsurance,
      ...(formattedFirstDue ? { firstDue: formattedFirstDue } : {}),
      documentDispatch: {
        dispatch: "electronic",
      },
      dispatchControl: {
        contract: dispatchContract,
        invoice: dispatchInvoice,
        reminder: dispatchReminder,
        offerEmail: dispatchEmail,
        orderEmail: dispatchEmail,
        agentEmail: agentDispatchEmail || undefined,
      },
      paymentMethod: paymentMethod,
      yearlyPaymentFrequency: {
        frequency: paymentInterval,
      },
      agentID: agentId || DEFAULT_AGENT_ID,
      ...(whitelabelPartner ? { whitelabelPartner } : {}),
      product: {
        productName: "pet",
        applicationQuestions: { // Added applicationQuestions
          terminationByInsurer: false,
          rejectedOrSpecialConditions: false,
        },
        pets: [
          {
            species: petType === "dog" ? "Dog" : "Cat",
            name: petName,
            birthdate: formattedPetBirthdate,
            breed: finalBreed,
            crossbreed: isCrossbreedFlag,
            gender: gender,
            castration: isCastrated,
            housecat: petType === "cat" ? isHouseCat : undefined,
            productBundle: selectedProduct.id.toUpperCase() as "S" | "M" | "L",
            ...(normalizedChipNumber ? { chipNumber: normalizedChipNumber, chipped: true } : {}),
            coverages: {
              disease: {
                requested: true,
                annualDeductible: yearlyDeductible,
                claimDeductiblePercentageUpTo9Yrs,
                claimDeductiblePercentageOver9Yrs,
                sumInsured: productDetails.disease.sum_insured,
                dentalTreatmentAccident: productDetails.disease.dental_treatment_accident,
                dentalTreatmentDisease: productDetails.disease.dental_treatment_disease,
                emergencyCare: productDetails.disease.emergency_care,
                preventiveTreatment: productDetails.disease.preventive_treatment,
                alternativeTreatment: productDetails.disease.alternative_treatment,
                advancedTreatment: productDetails.disease.advanced_treatment,
                medicalFood: productDetails.disease.medical_food,
                medicalFoodCap: productDetails.disease.medical_food_cap,
                behaviourTreatment: productDetails.disease.behaviour_treatment,
                emergencyTreatment: productDetails.disease.emergency_treatment,
              },
              preexistingConditions: {
                requested: preexistingCoverage,
                claimDeductible: productDetails.preexisting_condition.claim_deductible,
                claimDeductiblePercentageUpTo9Yrs: productDetails.preexisting_condition.claim_deductible_percentage_up_to_9_yrs,
                claimDeductiblePercentageOver9Yrs: productDetails.preexisting_condition.claim_deductible_percentage_over_9_yrs,
                sumInsuredCurrentYear: productDetails.preexisting_condition.sum_insured_current_year,
                sumInsuredNextYear: productDetails.preexisting_condition.sum_insured_next_year,
              },
              worldwideCoverage: {
                requested: worldwideCoverage,
                sumInsured: productDetails.worldwide_coverage.sum_insured,
              },
            },
          },
        ],
      },
      customer,
    };
  };

  const uploadPendingDocument = async (currentContractId: string, currentContractToken: string) => {
    if (!agentToken) return;
    if (!consultationProtocolPendingFile) return;
    try {
      const response = await uploadDocument(currentContractToken, currentContractId, {
        file: consultationProtocolPendingFile,
        filename: consultationProtocolPendingFilename || "consultation_protocol",
        kind: "consultation_protocol",
      });
      if (typeof response.documentId === "number") {
        setConsultationProtocolDocument(response.documentId, response.filename || consultationProtocolPendingFilename || "consultation_protocol");
        clearConsultationProtocolPending();
      } else {
        throw new Error("Missing document ID");
      }
    } catch (error) {
      console.error("Document upload failed:", error);
      await showErrorDialog(t("consultationProtocol.errors.uploadFailed"));
    }
  };

  const handlePetDetailsSubmitAndFetch = async (
    submittedPetName: string,
    selectedBreed: string,
    submittedBreedLabel: string | null,
    submittedBirthDate: Date | undefined,
    submittedSelectedBreedType: "purebred" | "crossbreed" | "mixed",
    submittedGender: "male" | "female" | "unknown",
    submittedIsCastrated: boolean,
    submittedIsHouseCat: boolean,
    submittedSelectedDogSize: "small" | "large" | null,
    submittedPreexistingCoverage: boolean,
    submittedChipNumber: string | null
  ) => {
    handlePetDetailsSubmitState(
      submittedPetName,
      selectedBreed,
      submittedBreedLabel,
      submittedBirthDate,
      submittedSelectedBreedType,
      submittedGender,
      submittedIsCastrated,
      submittedIsHouseCat,
      submittedSelectedDogSize,
      submittedPreexistingCoverage,
      submittedChipNumber
    );

    if (petType && submittedBirthDate) {
      const fetchedPrices = await fetchInitialBundlePrices(
        petType,
        submittedPetName,
        selectedBreed,
        submittedBirthDate,
        submittedSelectedBreedType,
        submittedGender,
        submittedIsCastrated,
        submittedIsHouseCat,
        submittedSelectedDogSize,
        submittedPreexistingCoverage,
        "Placeholder Name",
        "placeholder@example.com",
        currentLocale,
        agentId
      );

      if (fetchedPrices && !underwritingFailed) {
        setBundlePrices(fetchedPrices);
        navigate(getLocalizedPath('routes.productVariantSelection'));
      }
    }
  };

  const handleProductVariantSelectionAndNavigate = (product: ProductVariant) => {
    setSelectedProduct(product);
    navigate(getLocalizedPath('routes.productCustomization'));
  };

  const handleRequestOffer = async (
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    submittedStreet: string,
    submittedHousenumber: string,
    submittedCity: string,
    submittedZip: string,
    submittedCountry: string
  ) => {
    setIsSubmittingOffer(true);
    try {
      const offerPayload = buildOfferPayload(firstName, lastName, email, phone, submittedStreet, submittedHousenumber, submittedCity, submittedZip, submittedCountry);
      if (!offerPayload) {
        setIsSubmittingOffer(false);
        return;
      }
      const offerResponse = await createOffer(offerPayload);

      if (offerResponse.underwriting === false) {
        const reason = offerResponse.underwritingReason || offerResponse.feedbackReason;
        showErrorDialog(reason || t("underwritingError.defaultReason"));
        return;
      }

      if (offerResponse.grossPremiumProRata !== undefined) {
        setFinalPrice(offerResponse.grossPremiumProRata);
        handleContactDetailsSubmitState(firstName, lastName, email, phone, submittedStreet, submittedHousenumber, submittedCity, submittedZip, submittedCountry, contactBirthdate, customerGender || "");
        if (offerResponse.contractID && offerResponse.contractToken) {
          await uploadPendingDocument(offerResponse.contractID, offerResponse.contractToken);
        }
        // Redirect to external offer page
        window.location.href = t('externalLinks.offerPage');
      } else {
        throw new Error("API did not return grossPremiumProRata for the offer.");
      }
    } catch (err) {
      console.error("Error creating offer:", err);
      showErrorDialog(t("common.errorCalculatingPrice"));
    } finally {
      setIsSubmittingOffer(false);
    }
  };

  const handleCreateApplication = async (
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    submittedStreet: string,
    submittedHousenumber: string,
    submittedCity: string,
    submittedZip: string,
    submittedCountry: string
  ) => {
    setIsSubmittingOffer(true);
    try {
      const shouldReuseExistingOffer = offerLoadedFromUrl && !hasLeftConfirmationAfterOfferLoad && !!contractId && !!contractToken;
      let currentContractId = contractId;
      let currentContractToken = contractToken;

      if (!shouldReuseExistingOffer) {
        const offerPayload = buildOfferPayload(firstName, lastName, email, phone, submittedStreet, submittedHousenumber, submittedCity, submittedZip, submittedCountry);
        if (!offerPayload) {
          setIsSubmittingOffer(false);
          return;
        }
        const offerResponseForApplication = await createOffer(offerPayload);

        if (offerResponseForApplication.underwriting === false) {
          const reason = offerResponseForApplication.underwritingReason || offerResponseForApplication.feedbackReason;
          showErrorDialog(reason || t("underwritingError.defaultReason"));
          return;
        }

        currentContractId = offerResponseForApplication.contractID || null;
        currentContractToken = offerResponseForApplication.contractToken || null;
        // Keep state in sync with the offer we are about to apply
        if (offerResponseForApplication.grossPremiumProRata !== undefined) {
          setFinalPrice(offerResponseForApplication.grossPremiumProRata);
        } else {
          console.warn("Offer creation did not return grossPremiumProRata; continuing to apply.");
        }
      } else {
        // Reuse the loaded offer without creating a new one; just keep contact details in sync.
      }

      // Keep contact details in sync regardless of the path taken above
      handleContactDetailsSubmitState(firstName, lastName, email, phone, submittedStreet, submittedHousenumber, submittedCity, submittedZip, submittedCountry, contactBirthdate, customerGender || "");

      if (!currentContractId || !currentContractToken) {
        throw new Error("Offer creation failed to return contractID or contractToken.");
      }

      await uploadPendingDocument(currentContractId, currentContractToken);

      const formattedStartOfInsurance = format(startOfInsurance, "yyyy-MM-dd");
      const normalizedChipNumber = (chipNumber || "").replace(/\D/g, "").slice(0, 15);
      const applicationPayload: ApplicationRequest = {
        contractID: currentContractId,
        contractToken: currentContractToken,
        startOfInsurance: formattedStartOfInsurance,
        chipNumber: normalizedChipNumber || undefined,
      };

      const applicationResponse = await createApplication(applicationPayload);

      if (applicationResponse.underwriting === false) {
        const reason = applicationResponse.underwritingReason || applicationResponse.feedbackReason;
        navigate(getLocalizedPath('routes.underwritingError'), { state: { underwritingReason: reason, previousPath: getLocalizedPath('routes.addressDetails') } });
        return;
      }

      if (applicationResponse.grossPremiumProRata !== undefined) {
        setFinalPrice(applicationResponse.grossPremiumProRata);
        handleResetFunnel();
        // Redirect to external thank you page
        window.location.href = t('externalLinks.thankYouPage');
      } else {
        throw new Error("API did not return grossPremiumProRata for the application.");
      }
    } catch (err) {
      console.error("Error creating offer or application:", err);
      showErrorDialog(t("common.errorCalculatingPrice"));
    } finally {
      setIsSubmittingOffer(false);
    }
  };

  return {
    handlePetDetailsSubmitAndFetch,
    handleProductVariantSelectionAndNavigate,
    handleRequestOffer,
    handleCreateApplication,
    isSubmittingOffer,
  };
};
